import React from 'react';
import { Row, Col, Tag, Typography, Statistic, Space, Descriptions, Progress } from 'antd';
import { Sample } from '../../../../../types/lab/SampleTypes';
import { Patient } from '../../../../../types/healthcare/PatientTypes';
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	ExclamationCircleOutlined,
	UserOutlined,
	CalendarOutlined,
	ExperimentOutlined,
} from '@ant-design/icons';
import { SectionCard, StatusTag } from '../../../../../components/shared';
import dayjs from 'dayjs';

const { Text } = Typography;

interface SampleOverviewTabProps {
	sample: Sample;
	patient: Patient | null;
}

const SampleOverviewTab: React.FC<SampleOverviewTabProps> = ({ sample, patient }) => {


	const getProtocolProgress = () => {
		if (!sample.protocolInstances?.length) return 0;
		const completed = sample.protocolInstances.filter(p => p.status === 'completed').length;
		return Math.round((completed / sample.protocolInstances.length) * 100);
	};

	const getAnalysisProgress = () => {
		if (!sample.analyses?.length) return 0;
		const completed = sample.analyses.filter(a => a.status === 'completed').length;
		return Math.round((completed / sample.analyses.length) * 100);
	};

	const formatDate = (dateString: string | null | undefined) => {
		if (!dateString) return 'N/A';
		return dayjs(dateString).format('MMM DD, YYYY HH:mm');
	};

	const priorityCssColor = (level: number | undefined): string => {
		if (!level) return 'var(--mg-text-secondary)';
		if (level <= 3) return 'var(--mg-success)';
		if (level <= 6) return 'var(--mg-warning)';
		return 'var(--mg-danger)';
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Key Metrics */}
			<Row gutter={[16, 16]}>
				<Col span={6}>
					<SectionCard>
						<div style={{ textAlign: 'center' }}>
							<Statistic
								title={<Text style={{ fontSize: 12 }}>Processing Status</Text>}
								value={sample.sampleOA?.status || 'Unknown'}
								valueStyle={{ fontSize: 14, fontWeight: 700 }}
								prefix={
									sample.sampleOA?.status === 'complete' ?
										<CheckCircleOutlined style={{ color: 'var(--mg-success)' }} /> :
									sample.sampleOA?.status === 'processing' ?
										<ClockCircleOutlined style={{ color: 'var(--mg-warning)' }} /> :
										<ExclamationCircleOutlined style={{ color: 'var(--mg-text-secondary)' }} />
								}
							/>
							<div style={{ marginTop: 8 }}>
								<StatusTag status={sample.sampleOA?.status || 'unknown'} />
							</div>
						</div>
					</SectionCard>
				</Col>
				<Col span={6}>
					<SectionCard>
						<div style={{ textAlign: 'center' }}>
							<Statistic
								title={<Text style={{ fontSize: 12 }}>Priority Level</Text>}
								value={sample.priority_level || 1}
								valueStyle={{ fontSize: 14, fontWeight: 700 }}
								suffix="/10"
							/>
							<div style={{ marginTop: 8 }}>
								<Tag style={{ borderColor: `${priorityCssColor(sample.priority_level)}40`, background: `${priorityCssColor(sample.priority_level)}14`, color: priorityCssColor(sample.priority_level), fontSize: 12 }}>
									{sample.priority_level && sample.priority_level > 5 ? 'HIGH' : 'NORMAL'}
								</Tag>
							</div>
						</div>
					</SectionCard>
				</Col>
				<Col span={6}>
					<SectionCard>
						<div style={{ textAlign: 'center' }}>
							<Statistic
								title={<Text style={{ fontSize: 12 }}>Protocols Progress</Text>}
								value={getProtocolProgress()}
								valueStyle={{ fontSize: 14, fontWeight: 700 }}
								suffix="%"
							/>
							<Progress percent={getProtocolProgress()} size="small" showInfo={false} style={{ marginTop: 8 }} />
						</div>
					</SectionCard>
				</Col>
				<Col span={6}>
					<SectionCard>
						<div style={{ textAlign: 'center' }}>
							<Statistic
								title={<Text style={{ fontSize: 12 }}>Analysis Progress</Text>}
								value={getAnalysisProgress()}
								valueStyle={{ fontSize: 14, fontWeight: 700 }}
								suffix="%"
							/>
							<Progress percent={getAnalysisProgress()} size="small" showInfo={false} style={{ marginTop: 8 }} />
						</div>
					</SectionCard>
				</Col>
			</Row>

			{/* Sample Information */}
			<SectionCard title="Sample Information">
				<Descriptions size="small" column={3} colon={false}>
					<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Sample ID</Text>}>
						<Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{sample.sampleID}</Text>
					</Descriptions.Item>
					<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Client</Text>}>
						<Text style={{ fontSize: 12 }}>{sample.client}</Text>
					</Descriptions.Item>
					<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Sample Type</Text>}>
						<Text style={{ fontSize: 12 }}>{sample.sampleOA?.sampleType || 'N/A'}</Text>
					</Descriptions.Item>
					<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Created</Text>}>
						<Space size="small">
							<CalendarOutlined style={{ fontSize: 12 }} />
							<Text style={{ fontSize: 12 }}>{formatDate(sample.created_at)}</Text>
						</Space>
					</Descriptions.Item>
					<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Updated</Text>}>
						<Space size="small">
							<CalendarOutlined style={{ fontSize: 12 }} />
							<Text style={{ fontSize: 12 }}>{formatDate(sample.updated_at)}</Text>
						</Space>
					</Descriptions.Item>
					<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Status Changed</Text>}>
						<Text style={{ fontSize: 12 }}>{formatDate(sample.last_status_change)}</Text>
					</Descriptions.Item>
				</Descriptions>
			</SectionCard>

			{/* Patient Information */}
			{patient && (
				<SectionCard title="Patient Information">
					<Descriptions size="small" column={2} colon={false}>
						<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Name</Text>}>
							<Space size="small">
								<UserOutlined style={{ fontSize: 12 }} />
								<Text style={{ fontSize: 12 }}>{`${patient.first_name} ${patient.last_name}`}</Text>
							</Space>
						</Descriptions.Item>
						<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>DOB</Text>}>
							<Text style={{ fontSize: 12 }}>{patient.date_of_birth ? (typeof patient.date_of_birth === 'string' ? patient.date_of_birth : dayjs(patient.date_of_birth).format('MMM DD, YYYY')) : 'N/A'}</Text>
						</Descriptions.Item>
						<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Gender</Text>}>
							<Text style={{ fontSize: 12 }}>{patient.gender || 'N/A'}</Text>
						</Descriptions.Item>
						<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Phone</Text>}>
							<Text style={{ fontSize: 12 }}>{patient.phone || 'N/A'}</Text>
						</Descriptions.Item>
					</Descriptions>
				</SectionCard>
			)}

			{/* Quick Analysis Summary */}
			<Row gutter={[16, 16]}>
				<Col span={12}>
					<SectionCard title="Active Protocols">
						{sample.protocolInstances && sample.protocolInstances.length > 0 ? (
							<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
								{sample.protocolInstances.slice(0, 3).map((instance) => (
									<div key={instance.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
										<Text style={{ fontSize: 12 }}>{instance.protocol?.name}</Text>
										<StatusTag status={instance.status} />
									</div>
								))}
								{sample.protocolInstances.length > 3 && (
									<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>
										+{sample.protocolInstances.length - 3} more protocols
									</Text>
								)}
							</div>
						) : (
							<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>No protocols running</Text>
						)}
					</SectionCard>
				</Col>
				<Col span={12}>
					<SectionCard title="Analysis Results">
						{sample.analyses && sample.analyses.length > 0 ? (
							<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
								{sample.analyses.slice(0, 3).map((analysis) => (
									<div key={analysis.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
										<Text style={{ fontSize: 12 }}>{analysis.protocol_name}</Text>
										<StatusTag status={analysis.status} />
									</div>
								))}
								{sample.analyses.length > 3 && (
									<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>
										+{sample.analyses.length - 3} more analyses
									</Text>
								)}
							</div>
						) : (
							<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>No analysis results</Text>
						)}
					</SectionCard>
				</Col>
			</Row>

			{/* Data Summary */}
			<SectionCard title="Data Summary">
				<Row gutter={[16, 16]}>
					<Col span={8}>
						<Statistic
							title={<Text style={{ fontSize: 12 }}>QS Bases</Text>}
							value={sample.qsBases?.length || 0}
							valueStyle={{ fontSize: 14 }}
							prefix={<ExperimentOutlined />}
						/>
					</Col>
					<Col span={8}>
						<Statistic
							title={<Text style={{ fontSize: 12 }}>Protocol Instances</Text>}
							value={sample.protocolInstances?.length || 0}
							valueStyle={{ fontSize: 14 }}
						/>
					</Col>
					<Col span={8}>
						<Statistic
							title={<Text style={{ fontSize: 12 }}>Analysis Results</Text>}
							value={sample.analyses?.length || 0}
							valueStyle={{ fontSize: 14 }}
						/>
					</Col>
				</Row>
			</SectionCard>
		</div>
	);
};

export default SampleOverviewTab;
